import { PlusCircleIcon, XIcon } from '@langchain/untitled-ui-icons';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../utils/cn';
import { Badge } from '../Badge';
import { Command, CommandInput, CommandItem, CommandList } from './Command';

const SUGGESTION_LIMIT = 8;

export interface TagInputProps {
  /** Array of selected tag values */
  value: string[];
  /** Callback when tags are added or removed */
  onChange: (value: string[]) => void;
  /** Current search query */
  query: string;
  /** Callback when search query changes */
  onQueryChange: (query: string) => void;
  /** Available options filtered by query */
  filteredOptions: string[];
  /** Placeholder text for the input */
  placeholder?: string;
  /** Callback when a suggestion is selected */
  onSelectSuggestion: (suggestion: string) => void;
  /** Custom className for the container */
  className?: string;
  /** Disable the input */
  disabled?: boolean;
}

export const TagInput = ({
  value,
  onChange,
  query,
  onQueryChange: _onQueryChange,
  filteredOptions,
  placeholder,
  onSelectSuggestion,
  className,
  disabled = false,
}: TagInputProps) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(40);

  const handleRemove = useCallback(
    (idx: number) => {
      if (disabled) return;
      onChange(value.filter((_, valueIdx) => valueIdx !== idx));
    },
    [onChange, value, disabled]
  );

  const limitedSuggestions = filteredOptions.slice(0, SUGGESTION_LIMIT);

  const handleSelect = useCallback(
    (selectedPath: string) => {
      if (disabled) return;
      if (!value.includes(selectedPath)) {
        onSelectSuggestion(selectedPath);
      }
      setOpen(false);
    },
    [value, onSelectSuggestion, disabled]
  );

  const onQueryChange = useCallback(
    (query: string) => {
      if (disabled) return;
      _onQueryChange(query);
      setOpen(query.length > 0);
    },
    [_onQueryChange, disabled]
  );

  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        setContainerHeight(height);
      }
    };

    // Update height when value changes (tags added/removed)
    const timeoutId = setTimeout(updateContainerHeight, 0);

    return () => clearTimeout(timeoutId);
  }, [value]);

  return (
    <Command
      shouldFilter={false}
      className={cn('relative overflow-visible', className)}
    >
      <div
        ref={containerRef}
        className={cn(
          'flex flex-wrap gap-1 rounded-md border border-secondary p-1 outline-none',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {value.map((path, idx) => (
          <Badge
            key={`${path}-${idx}`}
            color="plain"
            rounded="sm"
            rightDecorator={!disabled ? XIcon : undefined}
            onClick={!disabled ? () => handleRemove(idx) : undefined}
            className={cn(
              'self-center',
              !disabled && 'cursor-pointer',
              disabled && 'cursor-not-allowed'
            )}
          >
            {path}
          </Badge>
        ))}

        <CommandInput
          ref={inputRef}
          value={query}
          onValueChange={onQueryChange}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (disabled) return;
            if (event.key === 'Backspace' && query.length === 0) {
              onChange(value.slice(0, -1));
            }
            if (event.key === 'ArrowDown' && !open) {
              event.preventDefault();
              setOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="h-6 flex-1 border-none bg-transparent py-0 pl-2 text-xs outline-none disabled:cursor-not-allowed"
          aria-label="Tag input"
          aria-expanded={open}
          aria-controls="tag-suggestions"
          aria-autocomplete="list"
        />
      </div>

      <CommandList
        id="tag-suggestions"
        className={cn(
          'absolute left-0 right-0 z-10 rounded-md border border-secondary bg-primary',
          (!open || disabled) && 'hidden'
        )}
        style={{ top: `${containerHeight}px` }}
      >
        {limitedSuggestions.length === 0 && query.trim() && (
          <CommandItem
            value={query}
            onSelect={() => handleSelect(query)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm aria-selected:bg-secondary"
          >
            <PlusCircleIcon className="h-4 w-4" /> {query}
          </CommandItem>
        )}

        {limitedSuggestions.map((option) => (
          <CommandItem
            key={option}
            value={option}
            onSelect={() => handleSelect(option)}
            className="px-3 py-1.5 text-sm aria-selected:bg-secondary"
          >
            {option}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
};
